import React, { useState } from 'react';
import { Upload, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useReservationPhotos, useDeletePhoto } from '../../hooks/usePhotos';
import { Avatar } from '../auth/Profile';
import UploadPhotoModal from '../gallery/UploadPhotoModal';
import './ReservationPhotos.css';

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function Lightbox({ photos, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const photo = photos[index];

  function handleKeyDown(e) {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + photos.length) % photos.length);
    if (e.key === 'ArrowRight') setIndex(i => (i + 1) % photos.length);
  }

  return (
    <div className="lightbox-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={0} autoFocus>
      <button className="lightbox-close" onClick={onClose}><X size={24} /></button>
      {photos.length > 1 && (
        <>
          <button className="lightbox-nav lightbox-prev" onClick={e => { e.stopPropagation(); setIndex(i => (i - 1 + photos.length) % photos.length); }}>
            <ChevronLeft size={32} />
          </button>
          <button className="lightbox-nav lightbox-next" onClick={e => { e.stopPropagation(); setIndex(i => (i + 1) % photos.length); }}>
            <ChevronRight size={32} />
          </button>
        </>
      )}
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        <img src={photo.url} alt={photo.caption || photo.filename} className="lightbox-img" />
        {(photo.caption || photo.uploader_name) && (
          <div className="lightbox-meta">
            {photo.caption && <p className="lightbox-caption">{photo.caption}</p>}
            {photo.uploader_name && (
              <p className="lightbox-uploader">
                <Avatar name={photo.uploader_name} size="small" avatarUrl={photo.uploader_avatar} />
                {photo.uploader_name} · {timeAgo(photo.created_at)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReservationPhotos({ reservationId }) {
  const { user } = useAuth();
  const { photos, isLoading } = useReservationPhotos(reservationId);
  const deletePhoto = useDeletePhoto();
  const [showUpload, setShowUpload] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const isAdminOrManager = user.role === 'admin' || user.role === 'manager';

  async function handleDelete(e, photoId) {
    e.stopPropagation();
    if (!window.confirm('Delete this photo?')) return;
    await deletePhoto.mutateAsync(photoId);
    if (lightboxIndex !== null) setLightboxIndex(null);
  }

  return (
    <div className="reservation-photos">
      <div className="reservation-photos-header">
        <h3>Photos</h3>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowUpload(true)}>
          <Upload size={14} /> Add Photo
        </button>
      </div>

      {isLoading && <p className="rp-status">Loading photos…</p>}

      {!isLoading && photos.length === 0 && (
        <div className="rp-empty">
          <p>No photos shared yet.</p>
          <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}>
            <Upload size={14} /> Share the first photo
          </button>
        </div>
      )}

      {photos.length > 0 && (
        <div className="rp-grid">
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              className="rp-card"
              onClick={() => setLightboxIndex(idx)}
            >
              <div className="rp-thumb">
                <img src={photo.url} alt={photo.caption || photo.filename} loading="lazy" />
              </div>
              <div className="rp-meta">
                <Avatar name={photo.uploader_name} size="small" avatarUrl={photo.uploader_avatar} />
                <span className="rp-uploader">{photo.uploader_name}</span>
                <span className="rp-time">{timeAgo(photo.created_at)}</span>
              </div>
              {photo.caption && <p className="rp-caption">{photo.caption}</p>}
              {(photo.user_id === user.id || isAdminOrManager) && (
                <button
                  className="rp-delete-btn"
                  onClick={e => handleDelete(e, photo.id)}
                  title="Delete photo"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadPhotoModal
          onClose={() => setShowUpload(false)}
          defaultReservationId={reservationId}
        />
      )}

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
